import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ConfiguratorViewerHandle } from "../../components/ConfiguratorViewer";
import { useHostActions } from "./actions/useHostActions";
import type { ActionHostApi } from "./actions/types";
import { fetchCatalog, fetchDesigner, fetchProductDetail, fetchProductIds } from "./api/catalogClient";
import { fetchPriceBreakdown, type PriceBreakdownDto } from "./api/priceClient";
import { fetchDesignerRules } from "./api/rulesClient";
import { computeForcedBindingDelta, computeSeedTransitions, evaluate } from "../../rules";
import type {
  ConditionResults,
  ConfigurationState,
  EvalResult,
  ProductInfo,
  Rule,
  RuleCatalog,
} from "../../rules";
import { composeCartModal } from "./components/composeCartModal";
import { composeEmailModal } from "./components/composeEmailModal";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { escapeMessageHtml, groupInstancesToRows } from "./api/pdfRender";
import { DownloadModelModal } from "./components/DownloadModelModal";
import { RenderSceneModal } from "./components/RenderSceneModal";
import { coerceSlotMapping, mergeSlotMappings } from "./blocks/slotMapping";
import { Slot } from "./blocks/Slot";
import { ThemeStyle } from "./components/ThemeStyle";
import { NotificationsHost, useNotifications } from "./components/NotificationsHost";
import { FocusHost } from "./focus/FocusHost";
import { isMockStatePreset } from "./focus/mockState";
import { parseMeasurementPayload, type MeasurementSetup } from "./measurement";
import { pickLayout, pickSeriesPicker, DEFAULT_LAYOUT_ID, getLayoutDefaultMapping, canonicalLayoutId } from "./layouts/registry";
import type { LayoutProps } from "./layouts/types";
import { ActiveModalRenderer } from "./modals/ActiveModalRenderer";
import { BusyOverlay } from "./modals/BusyOverlay";
import { getModal, registerModal } from "./modals/registry";
import type { ModalStack } from "./modals/types";
import { useIsMobile } from "./useIsMobile";
import {
  defaultLayout,
  type DesignerConfigResponse,
  type DesignerSeriesDto,
  type SceneCatalogResponse,
  type SceneCategoryDto,
  type SceneDesignerLayout,
  type SceneInstance,
  type SceneProductDetailResponse,
  type SceneProductDto,
} from "./types";

export interface SceneDesignerToolProps {
  baseUrl?: string;
  apiBaseUrl?: string;
  /** Unity target name — required for a working viewer (e.g. "scene-designer"). */
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /**
   * Designer row id (Guid). When set, the tool fetches the configured Designer
   * payload and applies its catalog scope (series), layout toggles, theme, and
   * Unity build. When omitted, falls back to the demo-mode catalog endpoint
   * (every visible category, default layout).
   */
  designerId?: string;
  /**
   * Dashboard-preview opt-in for archived Designers. The public embed
   * endpoint 404s archived rows; passing this flag forwards `allowArchived=1`
   * on the fetch so the dashboard's in-app preview can still load them.
   */
  allowArchived?: boolean;
  /**
   * Per-subcomponent visibility. Code-level overrides — merged over the Designer's
   * layout-toggles (which are merged over defaultLayout). Pass nothing in normal use.
   */
  layout?: Partial<SceneDesignerLayout>;
  /**
   * Force a particular layout template id, ignoring whatever the Designer pins.
   * Mostly for dev/preview; production renders whatever the Designer chose.
   */
  layoutTemplate?: string;
  /**
   * Focus-mode preview target. When set, the tool skips the full configurator
   * and renders just one slot through <FocusHost>. Format: "slot:<slotId>"
   * (modal variants land in commit 3). Used by the dashboard's slot-mapping
   * editor for scoped live preview.
   */
  focus?: string;
  /**
   * Mock scene state used when in focus mode. Pairs with `focus`. Recognised
   * values come from MockStatePreset; unknown values fall back to "default".
   */
  mockState?: string;
  /**
   * Short language code (e.g. "en", "dk") forwarded to the catalog/designer/product
   * fetches so DVar text (product/category names, descriptions, material labels) is
   * translated. Unknown or blank codes fall back to the primary language server-side.
   */
  lang?: string;
  /**
   * Market unique id (e.g. "sweden") forwarded to the price endpoint so the
   * breakdown uses that market's pricelist + VAT. Omitted → the server falls
   * back to the default market / sole pricelist, so single-market installs
   * price correctly without any plumbing.
   */
  market?: string;
}

interface ResolvedConfig {
  categories: SceneCategoryDto[];
  products: SceneProductDto[];
  viewer: SceneCatalogResponse["viewer"];
  series: DesignerSeriesDto[];
  layoutToggles: Partial<SceneDesignerLayout> | null;
  layoutTemplate: string;
  slotMappingOverride: Record<string, unknown> | null;
  themeTokens: Record<string, string> | null;
  themeFontImportUrl: string | null;
  designerName: string | null;
  brandLogoUrl: string | null;
  /** Decimal separator ("." or ",") for end-user measurement readouts, already
   *  resolved server-side against the Designer setting + active language. */
  decimalSeparator: string;
}

/**
 * Drag-and-drop scene builder. This file is the *host*: it owns scene state,
 * fetches the Designer config, applies the theme, handles the series-picker
 * gate, then dispatches to whichever <Layout> component the Designer chose.
 * Layouts (under ./layouts/) are pure renderers and never fetch.
 *
 * Scene-state ownership: React holds the canonical list of placed instances.
 * Each placed product has an `instanceId` (minted by the JS drag layer, echoed
 * back by Unity's lifecycle events). Unity is a renderer — it reports facts
 * (ProductPlaced / ProductSelected / ProductRemoved keyed by instanceId) and
 * applies what React tells it. React keys everything on instanceId; `productId`
 * is the catalog id, shared by every copy of the same product.
 *
 * Configuration flow:
 *   With designerId → fetch /designer/{id}, apply its catalog scope, layout,
 *     theme, and Unity build. Shows a series-picker first when the Designer has
 *     2+ series; drops straight in for a single-series Designer.
 *   Without designerId → fall back to the bare /catalog endpoint (every visible
 *     category, default layout). Useful for development and the demo URL.
 */
export function SceneDesignerTool(props: SceneDesignerToolProps) {
  // ── Focus-mode short-circuit ─────────────────────────────────────────
  // When the embed URL carries `?focus=slot:<id>&mockState=<preset>`, skip
  // the full configurator entirely and render a separate component tree.
  // FocusHost has its own hooks; running it inside SceneDesignerTool's
  // hook list would violate rules-of-hooks because the hook count differs
  // between focus and normal renders. The split here is at the component
  // boundary so each tree's hook order stays stable.
  if (props.focus && props.designerId) {
    const mockState = isMockStatePreset(props.mockState) ? props.mockState : "default";
    return (
      <FocusHost
        designerId={props.designerId}
        baseUrl={props.baseUrl}
        apiBaseUrl={props.apiBaseUrl}
        target={props.target}
        stage={props.stage}
        focus={props.focus}
        mockState={mockState}
        lang={props.lang}
      />
    );
  }
  return <SceneDesignerToolInner {...props} />;
}

function SceneDesignerToolInner(props: SceneDesignerToolProps) {
  const [config, setConfig] = useState<ResolvedConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // When the Designer has >1 series, the user must pick one before the
  // configurator renders. Held locally; resetting clears placed instances.
  const [selectedSeries, setSelectedSeries] = useState<DesignerSeriesDto | null>(null);

  // Viewport class — the cart picks its mobile vs. desktop body from this.
  const isMobile = useIsMobile();

  // Canonical scene state: every placed product, keyed by instanceId.
  const [instances, setInstances] = useState<SceneInstance[]>([]);
  // Default material per collection — the user's left-panel picks. New
  // spawns inherit these via the drag layer; the on-select / cart paths
  // continue to read materialPicks on each SceneInstance. collectionId →
  // { materialId, dmatAssetId }. Cleared on series change.
  const [collectionPicks, setCollectionPicks] = useState<Record<string, { materialId: string; dmatAssetId: string | null }>>({});

  // ── Form-state store ────────────────────────────────────────────────
  // Slot-driven form blocks (input-* + form-submit) read + write here. Keyed
  // by formId → fieldName → value. formId defaults to "default" so single-
  // form slots work without operator setup. Cleared by openModal's reset
  // path so re-opening a form-bearing modal starts blank.
  const [formValues, setFormValues] = useState<Record<string, Record<string, string>>>({});
  const setFormValue = useCallback((formId: string, name: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [formId]: { ...(prev[formId] ?? {}), [name]: value },
    }));
  }, []);
  const readFormValues = useCallback((formId: string): Record<string, string> => {
    return formValues[formId] ?? {};
  }, [formValues]);
  const clearForm = useCallback((formId: string) => {
    setFormValues(prev => {
      if (!prev[formId]) return prev;
      const { [formId]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [isFpsCamera, setIsFpsCamera] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(true);

  // Modal stack — later entries layer on top. Cart, help, pdf-email etc. all
  // live here now; the cartOpen boolean retired.
  const [modalStack, setModalStack] = useState<ModalStack>([]);

  // Busy overlay (transient; shown during action.run network work).
  const [busy, setBusy] = useState<{ visible: boolean; label?: string | null }>({ visible: false });

  // Confirm dialog state. Populated by host.openConfirm; the dialog itself
  // is rendered next to the modal stack. Single-slot — back-to-back confirm
  // requests just replace each other rather than queue (the action layer
  // doesn't fire chained confirms in practice).
  const [confirmDialog, setConfirmDialog] = useState<{
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
  } | null>(null);

  // Typed scene-feature slice. Just measurements for v1; grows as features
  // land (.design.md Axis 7).
  const [measurementsVisible, setMeasurementsVisible] = useState(false);

  // Latest measurement setup Unity projected (width + height lines in screen
  // space). Null = nothing to draw (scene empty or no event yet). Updated from
  // the "SetupMeasurement" event; the overlay renders from this last payload.
  const [measurementSetup, setMeasurementSetup] = useState<MeasurementSetup | null>(null);

  // Current scene dimensions in metres, exposed via getFeature("sceneDimensions")
  // for the stat kind=measurement block (cart totals). Fed live by the
  // SetupMeasurement event's `dimensions`; null when the scene is empty / unknown.
  const [sceneDimensions, setSceneDimensions] =
    useState<{ width: number; height: number; depth: number } | null>(null);

  // Live price breakdown for the current scene, exposed via
  // getFeature("totalPrice") (price stat block) and
  // getFeature("priceBreakdown") (cart line prices / PDF quote). Null when
  // the host has no price source (NullPriceSource → 204) or the scene is
  // empty — the price UI renders nothing, exactly as before.
  const [priceBreakdown, setPriceBreakdown] = useState<PriceBreakdownDto | null>(null);

  const detailCacheRef = useRef<Map<string, SceneProductDetailResponse>>(new Map());
  const viewerRef = useRef<ConfiguratorViewerHandle>(null!);
  const [viewerReady, setViewerReady] = useState(false);

  // ── Fetch on mount / when params change ─────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setSelectedSeries(null);

    const fetchPromise = props.designerId
      ? fetchDesigner({
          baseUrl: props.baseUrl,
          designerId: props.designerId,
          target: props.target,
          stage: props.stage,
          allowArchived: props.allowArchived,
          lang: props.lang,
          signal: ctrl.signal,
        }).then(designerResponseToConfig)
      : fetchCatalog({
          baseUrl: props.baseUrl,
          target: props.target,
          stage: props.stage,
          lang: props.lang,
          signal: ctrl.signal,
        }).then(catalogResponseToConfig);

    fetchPromise
      .then(resp => {
        setConfig(resp);
        // Single-series Designer drops straight in.
        if (resp.series.length === 1) setSelectedSeries(resp.series[0]);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.target, props.stage, props.designerId, props.lang]);

  // ── Compute the layout flags the tool actually renders ──────────────
  // Order: code-level prop > Designer's stored toggles > defaults. Lets a
  // dashboard preview override one flag without losing the Designer's config.
  const layoutFlags: SceneDesignerLayout = useMemo(
    () => ({
      ...defaultLayout,
      ...(config?.layoutToggles ?? {}),
      ...(props.layout ?? {}),
    }),
    [config, props.layout]
  );

  // ── Notifications ───────────────────────────────────────────────────
  // Resolve the global `notifications` slot (merged: layout default + Designer
  // override) and feed it to the toast controller. notify() is threaded onto
  // the block host so any block can fire a trigger; matching notification
  // blocks surface as top-of-screen toasts. Resolved here (before the early
  // returns) so the controller hook runs unconditionally.
  const notificationEntries = useMemo(() => {
    if (!config) return [];
    const layoutIdForNotifs = props.layoutTemplate ?? config.layoutTemplate ?? DEFAULT_LAYOUT_ID;
    const merged = mergeSlotMappings(
      getLayoutDefaultMapping(layoutIdForNotifs),
      coerceSlotMapping(config.slotMappingOverride)
    );
    return merged["notifications"] ?? [];
  }, [config, props.layoutTemplate]);
  const notifications = useNotifications(notificationEntries);

  // Bridge the drag JS's "cancelled drag" DOM event (dropped outside the canvas
  // / interrupted) to the notification system. The drag layer lives in
  // configurator-viewer.js and has no React handle, so it dispatches a
  // document-level CustomEvent we relay to the `drag-cancelled` trigger.
  const notify = notifications.notify;
  useEffect(() => {
    const onCancelled = () => notify("drag-cancelled");
    document.addEventListener("dconfig:drag-cancelled", onCancelled);
    return () => document.removeEventListener("dconfig:drag-cancelled", onCancelled);
  }, [notify]);

  // ── Catalog scoped to the current series ────────────────────────────
  // When a series is active and the Designer has >1 series, we further restrict
  // the catalog to the picked series' subtree so other series don't bleed in.
  const visibleCategories = useMemo(() => {
    if (!config) return [];
    if (!selectedSeries || config.series.length <= 1) return config.categories;
    const allowed = new Set<number>();
    const childrenByParent = new Map<number | null, SceneCategoryDto[]>();
    for (const c of config.categories) {
      const key = c.parentId;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key)!.push(c);
    }
    const walk = (id: number) => {
      allowed.add(id);
      for (const child of childrenByParent.get(id) ?? []) walk(child.id);
    };
    walk(selectedSeries.rootCategoryId);
    return config.categories.filter(c => allowed.has(c.id));
  }, [config, selectedSeries]);

  // Refs that mirror state used by handleUnityEvent — its deps are empty
  // (we don't want to re-subscribe Unity event listeners on every state
  // change), so we read live values through these instead.
  const collectionPicksRef = useRef(collectionPicks);
  collectionPicksRef.current = collectionPicks;
  const productByIdRef = useRef<Map<string, SceneProductDto>>(new Map());

  const productById = useMemo(() => {
    const m = new Map<string, SceneProductDto>();
    for (const p of config?.products ?? []) m.set(p.id, p);
    productByIdRef.current = m;
    return m;
  }, [config]);

  const selectedInstance = useMemo(
    () => instances.find(i => i.instanceId === selectedInstanceId) ?? null,
    [instances, selectedInstanceId]
  );
  const selectedProductName = selectedInstance
    ? productById.get(selectedInstance.productId)?.name ?? null
    : null;

  // ── Unity → React: lifecycle events keyed by instanceId ─────────────
  const handleUnityEvent = useCallback((eventType: string, payload: string) => {
    // Measurement overlay setup — projected width/height lines for the current
    // scene, or a teardown when it empties. Has its own payload shape, so it's
    // parsed + handled before the instance-event parsing below. We always stash
    // the latest (even when measurements are toggled off) so flipping the toggle
    // on shows the current setup without waiting for the next Unity change.
    if (eventType === "SetupMeasurement") {
      const m = parseMeasurementPayload(payload);
      setMeasurementSetup(m && m.kind === "setup" ? m : null);
      // Keep the scene-dimensions feature in sync — the setup payload carries the
      // world-axis size, which the cart's measurement stat reads via getFeature.
      setSceneDimensions(m && m.kind === "setup" ? m.dimensions : null);
      return;
    }

    let parsed: { instanceId?: string; productId?: string; groupId?: string; groupName?: string } = {};
    try {
      parsed = payload ? (JSON.parse(payload) as typeof parsed) : {};
    } catch {
      /* some events send a bare string or empty body */
    }

    if (eventType === "ProductPlaced") {
      const { instanceId, productId, groupId, groupName } = parsed;
      if (!instanceId || !productId) return;
      // Analytics: a placed product is the Designer's "product selected" signal
      // (the Products report reads product.selected). Unity fires ProductPlaced
      // once per real placement, so this counts each placement once.
      (window as { DConfigAnalytics?: { track?: (t: string, d?: unknown) => void } })
        .DConfigAnalytics?.track?.("product.selected", { productId });
      // Seed materialPicks from the user's left-panel collection picks so
      // React's view of the new instance matches what Unity actually
      // rendered (DragStart sent the same bindings to Unity). Walks the
      // product's surfaceCollections; for each collection the user picked,
      // records the picked material id under the surface key. Surfaces
      // whose collection has no pick stay absent — the cart, selection
      // panel, etc. fall back to the product detail's defaultMaterialId.
      const initialPicks: Record<string, string> = {};
      const product = productByIdRef.current.get(productId);
      const surfaceMap = product?.surfaceCollections;
      if (surfaceMap) {
        const picks = collectionPicksRef.current;
        for (const [surfaceKey, collectionId] of Object.entries(surfaceMap)) {
          const pick = picks[collectionId];
          if (pick) initialPicks[surfaceKey] = pick.materialId;
        }
      }
      setInstances(prev =>
        prev.some(i => i.instanceId === instanceId)
          ? prev
          // Group membership is a Designer concept — when the placement event
          // carries one (the Designer assigned it, or learned from Unity which
          // products are connected), the instance records it so "designer-
          // group" sorting/grouping in the cart + PDF works. Absent → ungrouped.
          : [...prev, { instanceId, productId, materialPicks: initialPicks,
                        groupId: groupId || undefined, groupName: groupName || undefined }]
      );
      return;
    }

    if (eventType === "ProductSelected") {
      const { instanceId, productId } = parsed;
      if (!instanceId) return;
      setSelectedInstanceId(instanceId);
      if (productId) {
        setInstances(prev =>
          prev.some(i => i.instanceId === instanceId)
            ? prev
            : [...prev, { instanceId, productId, materialPicks: {} }]
        );
      }
      return;
    }

    if (eventType === "ProductDeselected") {
      setSelectedInstanceId(null);
      return;
    }

    if (eventType === "ProductRemoved") {
      const { instanceId } = parsed;
      if (!instanceId) return;
      setInstances(prev => prev.filter(i => i.instanceId !== instanceId));
      setSelectedInstanceId(prev => (prev === instanceId ? null : prev));
      return;
    }

    if (eventType === "VariantSwitched") {
      // Confirmation of a SwitchVariant we already sent — reconcile the
      // optimistic state in case Unity ended up on a different key than
      // expected. "main" maps to undefined so the field stays absent on
      // products at their default variant.
      const { instanceId, key } = parsed as { instanceId?: string; key?: string };
      if (!instanceId || typeof key !== "string") return;
      setInstances(prev => prev.map(i => i.instanceId === instanceId
        ? { ...i, variantKey: key === "main" ? undefined : key }
        : i));
      return;
    }

    if (eventType === "InstanceGrouped") {
      // The Designer (or Unity, once it computes which placed products are
      // physically connected) re-assigns an instance's group. Updates
      // membership in place so the cart + PDF regroup live. Empty groupId
      // clears membership (instance returns to "ungrouped").
      const { instanceId, groupId, groupName } = parsed;
      if (!instanceId) return;
      setInstances(prev => prev.map(i => i.instanceId === instanceId
        ? { ...i, groupId: groupId || undefined, groupName: groupName || undefined }
        : i));
      return;
    }
  }, []);

  // ── React → Unity: command verbs (CommandsReceiver inbox methods) ───
  // Bare verbs send empty body; verbs that need a payload (e.g.
  // SetSceneObjectMaterials) pass the second arg and we JSON-encode here so
  // every caller stays uniform.
  const sendCommand = useCallback((command: string, payload?: unknown) => {
    const v = viewerRef.current;
    if (!v) return;
    const body = payload === undefined ? "" : JSON.stringify(payload);
    v.sendToUnity(command, body).catch(err =>
      console.warn(`[SceneDesigner] ${command} failed:`, err)
    );
    if (command === "EnableFPSCamera") setIsFpsCamera(true);
    if (command === "EnableTopDownCamera") setIsFpsCamera(false);
  }, []);

  const handleResetScene = useCallback(() => {
    sendCommand("ResetScene");
    setInstances([]);
    setSelectedInstanceId(null);
    // Drop the measurement overlay — the scene is now empty. Unity sends a
    // fresh "SetupMeasurement" once products are placed again; until then there
    // shouldn't be stale lines hanging over an empty viewer.
    setMeasurementSetup(null);
  }, [sendCommand]);

  const handleDeleteSelected = useCallback(() => {
    const v = viewerRef.current;
    if (!v || !selectedInstanceId) return;
    v.sendToUnity("RemoveInstance", JSON.stringify({ instanceId: selectedInstanceId })).catch(err =>
      console.warn("[SceneDesigner] RemoveInstance failed:", err)
    );
  }, [selectedInstanceId]);

  // Core pick path: write one surface pick into an instance's canonical
  // materialPicks and push the resolved DMat binding to Unity. The user-facing
  // swatch tap (handlePickMaterial) and the rules-engine seed path both come
  // through here, so a seeded material behaves exactly like a user pick.
  const applyPickToInstance = useCallback(
    (instanceId: string, productId: string, surfaceKey: string, materialId: string) => {
      setInstances(prev =>
        prev.map(i =>
          i.instanceId === instanceId
            ? { ...i, materialPicks: { ...i.materialPicks, [surfaceKey]: materialId } }
            : i
        )
      );

      const detail = detailCacheRef.current.get(productId);
      if (!detail) {
        console.warn(
          `[SceneDesigner] pick-material aborted — product detail not yet cached for productId=${productId}.`
        );
        return;
      }
      const surface = detail.surfaces.find(s => s.surfaceKey === surfaceKey);
      if (!surface) {
        console.warn(
          `[SceneDesigner] pick-material aborted — surfaceKey="${surfaceKey}" not found on product detail (cached surfaces: ${detail.surfaces.map(s => s.surfaceKey).join(", ")}).`
        );
        return;
      }
      const material = surface.materials.find(m => m.id === materialId);
      if (!material) {
        console.warn(
          `[SceneDesigner] pick-material aborted — materialId="${materialId}" not found in surface "${surfaceKey}" materials.`
        );
        return;
      }
      if (!material.dmatAssetId) {
        console.warn(
          `[SceneDesigner] pick-material aborted — material "${material.name}" (id=${material.id}) has no DMatAsset id on the wire. Check the material's asset binding in the dashboard.`
        );
        return;
      }
      const v = viewerRef.current;
      if (!v) {
        console.warn("[SceneDesigner] pick-material aborted — viewerRef is null (Designer not ready).");
        return;
      }
      v.sendToUnity(
        "SetSurfaceMaterials",
        // Binding entries are PascalCase to match the Unity-side
        // MaterialBinding fields (Unity's JsonUtility is case-sensitive).
        // `instanceId` stays camelCase — it's an inline DTO field authored
        // that way. Same convention as the spawn-bundle's defaultBindings.
        JSON.stringify({
          instanceId,
          bindings: [{ Key: surfaceKey, DmatId: material.dmatAssetId }],
        })
      ).catch(err => console.warn("[SceneDesigner] SetSurfaceMaterials failed:", err));
    },
    []
  );

  const handlePickMaterial = useCallback(
    (surfaceKey: string, materialId: string) => {
      if (!selectedInstance) {
        console.warn("[SceneDesigner] pick-material aborted — no instance is selected.");
        return;
      }
      applyPickToInstance(selectedInstance.instanceId, selectedInstance.productId, surfaceKey, materialId);
    },
    [selectedInstance, applyPickToInstance]
  );

  // Bump on every cache write so dependents reading through the ref
  // re-evaluate. Without this, host methods like canToggleVariant (which
  // read detailCacheRef.current synchronously) would keep returning stale
  // false answers when a newly-placed instance's detail lands — the cache
  // would have it, but React wouldn't have re-rendered to consult it.
  const [detailCacheVersion, setDetailCacheVersion] = useState(0);
  const cacheDetail = useCallback((detail: SceneProductDetailResponse) => {
    detailCacheRef.current.set(detail.id, detail);
    setDetailCacheVersion(v => v + 1);
  }, []);

  // Product details carry translated DVar text (material/surface labels), so a
  // language switch must drop the cache — otherwise placed instances keep showing
  // the previous language's names until re-fetched. Cleared on lang change; the
  // lazy detail loader below refills for whatever's currently placed.
  useEffect(() => {
    detailCacheRef.current.clear();
    setDetailCacheVersion(v => v + 1);
  }, [props.lang]);

  // ── Rules engine (reactive mode) ────────────────────────────────────
  // The pure evaluator from src/rules runs against a snapshot of the scene
  // after every state change; its overlay drives locked/restricted swatches
  // in the blocks and forced-material pushes to the viewer. Rules load once
  // per Designer config; a fetch failure degrades to "no rules" — the
  // configurator must never break because the rules API is down.
  const [rules, setRules] = useState<Rule[]>([]);
  useEffect(() => {
    if (!props.designerId || !config) {
      setRules([]);
      return;
    }
    const ctrl = new AbortController();
    // `lang` rides along so each rule's message DVar arrives resolved as
    // `messageText` in the active language; a language switch refetches.
    fetchDesignerRules({ baseUrl: props.baseUrl, designerId: props.designerId, lang: props.lang, signal: ctrl.signal })
      .then(setRules)
      .catch(err => {
        if (err.name === "AbortError") return;
        console.warn("[SceneDesigner rules] rules fetch failed — continuing without rules:", err);
        setRules([]);
      });
    return () => ctrl.abort();
  }, [props.baseUrl, props.designerId, props.lang, config]);

  // Catalog adapter: the evaluator's fact resolvers (parameterValue,
  // materialPick defaults, restrictMaterials swatch lists) read products
  // through this. Surfaces come from the lazily-warmed detail cache — an
  // uncached product simply resolves with zero surfaces until its detail
  // lands (detailCacheVersion in the deps re-derives it then).
  const ruleCatalog: RuleCatalog = useMemo(() => {
    const byId = new Map<string, ProductInfo>();
    for (const p of config?.products ?? []) {
      const detail = detailCacheRef.current.get(p.id);
      byId.set(p.id, {
        productId: p.id,
        parameterValues: p.parameterValues ?? {},
        // hasConnector resolves to false in scene mode: connector keys ride
        // the Unity spawn bundle (hookSpecs), not the catalog wire — the
        // slot-configurator integration will supply them.
        connectorKeys: [],
        surfaces: (detail?.surfaces ?? []).map(s => ({
          surfaceKey: s.surfaceKey,
          defaultMaterialId: s.defaultMaterialId ?? undefined,
          materialIds: s.materials.map(m => m.id),
        })),
      });
    }
    return { getProduct: (id: string) => byId.get(id) };
  }, [config, detailCacheVersion]);

  // Evaluate on every relevant state change. Null when no rules are loaded —
  // blocks then render unconstrained and no push/seed effects fire.
  const ruleEval = useMemo<EvalResult | null>(() => {
    if (rules.length === 0) return null;
    const state: ConfigurationState = {
      instances: instances.map(i => ({
        instanceId: i.instanceId,
        productId: i.productId,
        variantKey: i.variantKey,
        materialPicks: i.materialPicks,
        // Scene mode has no slot wiring yet — Unity owns physical attachment
        // and doesn't report hook occupancy, so slot/assembly relations see
        // empty slots. The slot-configurator integration fills this in.
        slotContents: {},
        groupId: i.groupId,
      })),
      context: { designerId: props.designerId, lang: props.lang },
      formValues,
      collectionPicks: Object.fromEntries(
        Object.entries(collectionPicks).map(([id, pick]) => [id, pick.materialId])
      ),
    };
    return evaluate(state, rules, ruleCatalog);
  }, [instances, rules, collectionPicks, formValues, ruleCatalog, props.designerId, props.lang]);

  // Structured authoring errors (unknown facts, non-convergence, …) surface
  // once per evaluation change — the memo above only mints a new result when
  // an input changed, so this never spams per render.
  useEffect(() => {
    if (!ruleEval) return;
    for (const e of ruleEval.errors) {
      console.warn(
        `[SceneDesigner rules] ${e.kind}${e.ruleId ? ` (rule ${e.ruleId})` : ""}${e.instanceId ? ` on ${e.instanceId}` : ""}: ${e.message}`
      );
    }
  }, [ruleEval]);

  // Forced picks → viewer. The overlay is derived state; Unity is imperative —
  // so we push exactly the delta vs. what we last applied per instance
  // (computeForcedBindingDelta), and clearing a force pushes the canonical
  // pick back. Instances whose detail isn't cached are left un-marked so the
  // detailCacheVersion bump retries them once resolution is possible.
  const lastForcedPushRef = useRef<Map<string, Record<string, string>>>(new Map());
  useEffect(() => {
    // Prune bookkeeping for removed instances first — their Unity objects are
    // gone, nothing to restore.
    const live = new Set(instances.map(i => i.instanceId));
    for (const id of Array.from(lastForcedPushRef.current.keys())) {
      if (!live.has(id)) lastForcedPushRef.current.delete(id);
    }

    const v = viewerRef.current;
    if (!viewerReady || !v) return; // viewerReady in deps — retries when up

    for (const inst of instances) {
      const io = ruleEval?.overlay.instances[inst.instanceId];
      const nextForced: Record<string, string> = {};
      for (const [surfaceKey, entry] of Object.entries(io?.forcedPicks ?? {})) {
        nextForced[surfaceKey] = entry.materialId;
      }
      const prevForced = lastForcedPushRef.current.get(inst.instanceId) ?? {};
      if (Object.keys(nextForced).length === 0 && Object.keys(prevForced).length === 0) continue;

      const detail = detailCacheRef.current.get(inst.productId);
      if (!detail) {
        // Can't resolve DMat ids yet. Deliberately don't touch prevForced so
        // the next detailCacheVersion bump recomputes the same delta.
        continue;
      }

      // Canonical resolution for cleared forces: user pick, else authored
      // default — same fallback chain the picker renders.
      const canonical: Record<string, string> = {};
      for (const s of detail.surfaces) {
        const pick = inst.materialPicks[s.surfaceKey] ?? s.defaultMaterialId;
        if (pick) canonical[s.surfaceKey] = pick;
      }

      const delta = computeForcedBindingDelta(prevForced, nextForced, canonical);
      const applied: Record<string, string> = { ...prevForced };
      const bindings: { Key: string; DmatId: string }[] = [];
      for (const d of delta) {
        // Cleared forces drop out of the applied map whether or not the
        // restore push resolves — the force itself is gone either way.
        if (nextForced[d.surfaceKey] === undefined) delete applied[d.surfaceKey];
        if (d.materialId === null) continue; // cleared, no canonical pick — leave the viewer as-is
        const material = detail.surfaces
          .find(s => s.surfaceKey === d.surfaceKey)
          ?.materials.find(m => m.id === d.materialId);
        if (!material?.dmatAssetId) {
          console.warn(
            `[SceneDesigner rules] forced pick skipped — materialId="${d.materialId}" on surface "${d.surfaceKey}" has no resolvable DMatAsset for productId=${inst.productId}.`
          );
          continue; // stays out of `applied`, so a later cache/rule change retries
        }
        bindings.push({ Key: d.surfaceKey, DmatId: material.dmatAssetId });
        if (nextForced[d.surfaceKey] !== undefined) applied[d.surfaceKey] = d.materialId;
      }
      lastForcedPushRef.current.set(inst.instanceId, applied);
      if (bindings.length === 0) continue;
      // Binding entries are PascalCase to match Unity's MaterialBinding fields
      // (JsonUtility is case-sensitive); `instanceId` stays camelCase. Same
      // convention as handlePickMaterial / apply-to-all.
      v.sendToUnity(
        "SetSurfaceMaterials",
        JSON.stringify({ instanceId: inst.instanceId, bindings })
      ).catch(err => console.warn("[SceneDesigner rules] SetSurfaceMaterials failed:", err));
    }
  }, [ruleEval, instances, viewerReady, detailCacheVersion]);

  // seedMaterial transitions → canonical picks. The evaluator is stateless
  // about seeds: we keep the previous evaluation's condition outcomes and
  // apply exactly the pending seeds whose condition newly became true, via
  // the same pick path a user tap takes (state write + viewer push). Applied
  // once per transition — the resulting re-evaluation reports the same
  // condition as still-true, which is not a transition.
  const prevConditionResultsRef = useRef<ConditionResults | null>(null);
  useEffect(() => {
    if (!ruleEval) return;
    const transitions = computeSeedTransitions(prevConditionResultsRef.current, ruleEval.conditionResults);
    prevConditionResultsRef.current = ruleEval.conditionResults;
    if (transitions.length === 0) return;
    const transitioned = new Set(transitions);
    for (const seed of ruleEval.pendingSeeds) {
      if (!transitioned.has(seed.conditionKey)) continue;
      const inst = instances.find(i => i.instanceId === seed.instanceId);
      if (!inst) continue; // synthetic (auto-attach) binding — no scene instance to write onto
      applyPickToInstance(inst.instanceId, inst.productId, seed.surfaceKey, seed.materialId);
    }
  }, [ruleEval, instances, applyPickToInstance]);

  /**
   * Apply a material to every placed instance with a surface that's bound to
   * the given collection. Walks `instances`, looks up each product's cached
   * surfaces, finds surfaces whose materials include this materialId (cheap
   * test: if the picker would have offered this material for that surface,
   * it's in scope), updates React state, and fires SetSurfaceMaterials.
   *
   * Instances whose product-detail isn't cached are skipped — selecting them
   * once via the UI warms the cache; until they've been touched, "apply to all"
   * doesn't reach them. Pragmatic v1; warm-pass enhancement deferred.
   */
  /**
   * Single-tap on a swatch in the left-panel collection picker. Two effects:
   *  1. Saves the pick in collectionPicks so future spawns inherit it via
   *     the drag layer's DragStart bindings.
   *  2. Applies immediately to the currently-selected instance — finds every
   *     surface on the selection's product that's wired to this collection
   *     and calls handlePickMaterial for each.
   */
  const handlePickCollectionDefault = useCallback(
    (collectionId: string, materialId: string, dmatAssetId: string | null) => {
      if (!materialId) return;
      setCollectionPicks(prev => ({
        ...prev,
        [collectionId]: { materialId, dmatAssetId },
      }));

      if (!selectedInstance) return;
      const product = productById.get(selectedInstance.productId);
      const surfaceMap = product?.surfaceCollections;
      if (!surfaceMap) return;
      for (const [surfaceKey, surfaceCollectionId] of Object.entries(surfaceMap)) {
        if (surfaceCollectionId !== collectionId) continue;
        // handlePickMaterial walks the detail cache for the actual DmatAssetId
        // and fires SetSurfaceMaterials. Skips silently when detail isn't
        // warmed yet — pragmatic; matches apply-to-all semantics.
        handlePickMaterial(surfaceKey, materialId);
      }
    },
    [selectedInstance, productById, handlePickMaterial]
  );

  const handleApplyMaterialToAll = useCallback(
    (_collectionId: string, materialId: string, dmatAssetId: string | null) => {
      if (!dmatAssetId) {
        console.warn(
          `[SceneDesigner] apply-to-all aborted — material id=${materialId} has no DMatAsset id on the wire. Check the material's asset binding in the dashboard.`
        );
        return;
      }
      const v = viewerRef.current;
      if (!v) {
        console.warn("[SceneDesigner] apply-to-all aborted — viewerRef is null (Designer not ready).");
        return;
      }

      const updates: Array<{ instanceId: string; surfaceKey: string }> = [];
      for (const inst of instances) {
        const detail = detailCacheRef.current.get(inst.productId);
        if (!detail) continue;
        for (const surface of detail.surfaces) {
          if (surface.locked) continue;
          // If this surface's collection includes the material, the material
          // is "applicable" to it. The wire shape doesn't carry a collection
          // id per surface — we approximate by: surface.materials.includes
          // (materialId). That handles the common case where finishes blocks
          // are aligned with surface collections; mismatched collections
          // simply won't fire for this surface, which is the right behavior.
          if (surface.materials.some(m => m.id === materialId)) {
            updates.push({ instanceId: inst.instanceId, surfaceKey: surface.surfaceKey });
          }
        }
      }
      if (updates.length === 0) {
        console.warn(
          `[SceneDesigner] apply-to-all found no matching surfaces for material id=${materialId} across ${instances.length} instance(s). ` +
          `(Instances need product-detail cached + a surface whose materials list includes this materialId.)`
        );
        return;
      }
      console.log(
        `[SceneDesigner] apply-to-all firing ${updates.length} update(s) across ${new Set(updates.map(u => u.instanceId)).size} instance(s).`
      );

      // React state: bulk-update materialPicks across instances.
      setInstances(prev =>
        prev.map(i => {
          const surfacesForInstance = updates
            .filter(u => u.instanceId === i.instanceId)
            .map(u => u.surfaceKey);
          if (surfacesForInstance.length === 0) return i;
          const nextPicks = { ...i.materialPicks };
          for (const k of surfacesForInstance) nextPicks[k] = materialId;
          return { ...i, materialPicks: nextPicks };
        })
      );

      // Unity: one SetSurfaceMaterials per instance with the relevant bindings.
      // Binding entries are PascalCase to match Unity's MaterialBinding fields
      // (JsonUtility is case-sensitive). `instanceId` stays camelCase.
      const byInstance = new Map<string, { Key: string; DmatId: string }[]>();
      for (const u of updates) {
        if (!byInstance.has(u.instanceId)) byInstance.set(u.instanceId, []);
        byInstance.get(u.instanceId)!.push({ Key: u.surfaceKey, DmatId: dmatAssetId });
      }
      for (const [instanceId, bindings] of byInstance) {
        v.sendToUnity("SetSurfaceMaterials", JSON.stringify({ instanceId, bindings }))
          .catch(err => console.warn("[SceneDesigner] apply-to-all SetSurfaceMaterials failed:", err));
      }
    },
    [instances]
  );

  const handleCloseSelection = useCallback(() => {
    setSelectedInstanceId(null);
  }, []);

  // ── Modal stack management ──────────────────────────────────────────
  // openModal honours each modal's dismissOnOtherModalOpen flag — when a new
  // modal opens, any currently-open modal that doesn't want to layer pops off.
  //
  // Optional payload is stashed under the modal id so a render override can
  // read per-instance data the opening block carries (e.g. render-scene's
  // renderProfileId). Stored in a ref because the modal renderer doesn't
  // depend on stack identity for re-renders — the stack state change above
  // already triggers a render of the modal subtree.
  const modalPayloadsRef = useRef<Map<string, unknown>>(new Map());
  const openModal = useCallback((id: string, payload?: unknown) => {
    if (payload !== undefined) modalPayloadsRef.current.set(id, payload);
    setModalStack(prev => {
      const next = prev.filter(openId => {
        const def = getModal(openId);
        return def != null && !def.dismissOnOtherModalOpen;
      });
      if (next.includes(id)) return next; // already open — no-op
      return [...next, id];
    });
  }, []);
  const closeOneModal = useCallback((id: string) => {
    modalPayloadsRef.current.delete(id);
    setModalStack(prev => prev.filter(openId => openId !== id));
  }, []);
  const closeTopModal = useCallback(() => {
    setModalStack(prev => (prev.length === 0 ? prev : prev.slice(0, -1)));
  }, []);
  const closeAllModals = useCallback(() => {
    setModalStack(prev => (prev.length === 0 ? prev : []));
  }, []);

  // ── Busy overlay ────────────────────────────────────────────────────
  const showBusy = useCallback((label?: string) => {
    setBusy({ visible: true, label: label ?? null });
  }, []);
  const hideBusy = useCallback(() => {
    setBusy({ visible: false });
  }, []);

  // ── Series-picker reset (used by back-to-series action) ─────────────
  const backToSeriesPicker = useCallback(() => {
    setSelectedSeries(null);
    setInstances([]);
    setSelectedInstanceId(null);
    setModalStack([]);
    // Series-scoped — clear the left-panel collection picks so the next
    // series starts fresh.
    setCollectionPicks({});
    // Clear the measurement overlay so it doesn't hang over the picker / the
    // next series' empty scene. It returns when Unity sends a new setup.
    setMeasurementSetup(null);
  }, []);

  // Self-heal: an empty scene can't have a measurement, so drop any lingering
  // overlay whenever the instance list empties (covers every path that clears
  // the scene — reset, back-to-series, removing the last product — without each
  // having to remember). The overlay returns when Unity sends a fresh setup.
  useEffect(() => {
    if (instances.length === 0) {
      setMeasurementSetup(null);
      setSceneDimensions(null);
    }
  }, [instances.length]);

  // ── Live pricing ────────────────────────────────────────────────────
  // Debounced: aggregate the scene into cart rows, resolve their display SKUs
  // (the same resolver the cart pill uses — pricing keys on the resolved
  // ProductID, honouring manual SKU overrides), then ask the host's price
  // source for a breakdown. Any failure or 204 → null → price UI hides.
  const priceSignature = useMemo(
    () => instances
      .map(i => {
        const picks = Object.keys(i.materialPicks).sort().map(k => `${k}=${i.materialPicks[k]}`).join("&");
        return `${i.productId}|${picks}|${i.variantKey ?? ""}`;
      })
      .sort()
      .join("‖"),
    [instances],
  );
  useEffect(() => {
    if (instances.length === 0) {
      setPriceBreakdown(null);
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = groupInstancesToRows(instances, productByIdRef.current);
        // Resolve display SKUs; a resolver failure falls back to catalog ids
        // (those price only if the operator keyed prices by catalog id).
        const resolved = new Map<string, string>();
        try {
          const resp = await fetchProductIds({
            baseUrl: props.baseUrl,
            body: {
              items: rows.map(r => ({
                groupKey: r.groupKey,
                productId: r.productId,
                materialPicks: r.materialPicks,
              })),
            },
            signal: ctrl.signal,
          });
          for (const r of resp.results) {
            if (r.productId) resolved.set(r.groupKey, r.productId);
          }
        } catch { /* keep catalog-id fallback */ }

        const breakdown = await fetchPriceBreakdown({
          baseUrl: props.baseUrl,
          body: {
            items: rows.map(r => ({
              productId: resolved.get(r.groupKey) ?? r.productId,
              quantity: r.qty,
            })),
            market: props.market,
            designerId: props.designerId,
          },
          signal: ctrl.signal,
        });
        if (!cancelled) setPriceBreakdown(breakdown);
      } catch {
        if (!cancelled) setPriceBreakdown(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
    // priceSignature covers every pricing-relevant facet of `instances`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceSignature, instances.length === 0, props.baseUrl, props.market]);

  // ── sceneFeatures slice ─────────────────────────────────────────────
  // Tiny v1: a typed switch on the key. New features extend the switch and
  // any state slice they need. .design.md Axis 7.
  const getFeature = useCallback((key: string): unknown => {
    switch (key) {
      case "measurementsVisible": return measurementsVisible;
      case "sceneDimensions":     return sceneDimensions;
      case "decimalSeparator":    return config?.decimalSeparator ?? ".";
      case "totalPrice":
        return priceBreakdown
          ? {
              amount: priceBreakdown.total,
              currency: priceBreakdown.currency,
              formatted: priceBreakdown.formattedTotal ?? undefined,
            }
          : undefined;
      case "priceBreakdown":      return priceBreakdown ?? undefined;
      default: return undefined;
    }
  }, [measurementsVisible, sceneDimensions, config?.decimalSeparator, priceBreakdown]);
  const setFeature = useCallback((key: string, value: unknown) => {
    switch (key) {
      case "measurementsVisible": {
        const next = Boolean(value);
        setMeasurementsVisible(next);
        // Fire-and-forget Unity verb. The Unity-side receiver name is
        // SetMeasurementsVisible with payload {"visible": true|false} —
        // single InboxMethod, no envelope. If the active build doesn't
        // implement it yet the Unity log will warn; React state still
        // flips so the toggle button's `active` state is accurate.
        const v = viewerRef.current;
        if (v) {
          v.sendToUnity("SetMeasurementsVisible", JSON.stringify({ visible: next }))
            .catch(err => console.warn("[SceneDesigner] SetMeasurementsVisible failed:", err));
        }
        return;
      }
      default:
        console.warn(`[SceneDesigner] setFeature: unknown key "${key}".`);
    }
  }, []);

  // Fetch a DMat's JSON blob and forward it to Unity along with the target
  // object name. Used by the apply-scene-materials verb and the startup-slot
  // pipeline. Each call is independent — failure of one binding doesn't
  // affect siblings; bad ids just log and skip. The Unity inbox method
  // (ApplyMaterialToObject) handles missing GameObjects on its end.
  //
  // `slot` (optional) targets one material index on a multi-material renderer;
  // Unity replaces just that slot and leaves the others intact. Omitted → the
  // field is absent on the wire and Unity defaults to slot 0 (fully
  // backward-compatible with the pre-slot payload).
  const applyMaterialToObject = useCallback(async (objectName: string, dmatId: string, slot?: number) => {
    if (!objectName || !dmatId) return;
    const v = viewerRef.current;
    if (!v) return;
    const base = (props.baseUrl ?? "").replace(/\/+$/, "");
    const url = `${base}/api/asset/file/${dmatId}/json`;
    try {
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        console.warn(`[SceneDesigner] DMat ${dmatId} fetch failed (${res.status}) at ${url}; skipping ${objectName}.`);
        return;
      }
      const material = await res.json();
      const payload: { object: string; material: unknown; slot?: number } =
        { object: objectName, material };
      if (typeof slot === "number" && Number.isInteger(slot) && slot >= 0) payload.slot = slot;
      await v.sendToUnity("ApplyMaterialToObject", JSON.stringify(payload));
      console.log(`[SceneDesigner] ApplyMaterialToObject → "${objectName}"${payload.slot !== undefined ? ` slot ${payload.slot}` : ""} (DMat ${dmatId}) sent to Designer.`);
    } catch (err) {
      console.warn(`[SceneDesigner] ApplyMaterialToObject failed for ${objectName} (url=${url}):`, err);
    }
  }, [props.baseUrl]);

  // ── Variant switching ───────────────────────────────────────────────
  // Toggle the selected instance between its main variant and a target key.
  // Unity is authoritative about geometry, so we send SwitchVariant with the
  // full intended material state (Unity doesn't retain bindings across
  // switches). On the VariantSwitched event we flip the local instance's
  // variantKey to match — until then the UI optimistically updates so the
  // toggle button reflects the new state immediately.
  const canToggleVariant = useCallback((targetKey: string) => {
    if (!targetKey || !selectedInstance) return false;
    const detail = detailCacheRef.current.get(selectedInstance.productId);
    if (!detail) return false; // not warmed yet — disabled until cache fills
    const variants = detail.variants ?? [];
    return variants.includes(targetKey);
    // detailCacheVersion participates in the deps so a cache fill (e.g. the
    // selection panel warming a freshly placed instance's detail) re-evaluates
    // this predicate and the button stops being stale-disabled.
  }, [selectedInstance, detailCacheVersion]);

  const toggleVariant = useCallback((targetKey: string) => {
    if (!selectedInstance) {
      console.warn("[SceneDesigner] toggleVariant: no instance selected.");
      return;
    }
    if (!canToggleVariant(targetKey)) {
      console.warn(`[SceneDesigner] toggleVariant: product has no variant '${targetKey}'.`);
      return;
    }
    // Toggle: if we're already on the alternate, go back to main.
    const isOnAlternate = selectedInstance.variantKey === targetKey;
    const nextKey = isOnAlternate ? "main" : targetKey;

    // Build the full bindings array from current materialPicks + the product
    // detail so Unity gets surface-key → dmatId pairs (PascalCase, per spec).
    // Unity silently skips bindings whose surface isn't on the target variant,
    // so we send everything we know about — no need to filter.
    const detail = detailCacheRef.current.get(selectedInstance.productId);
    const bindings: { Key: string; DmatId: string }[] = [];
    if (detail) {
      for (const surface of detail.surfaces) {
        const pickedId = selectedInstance.materialPicks[surface.surfaceKey] ?? surface.defaultMaterialId;
        if (!pickedId) continue;
        const mat = surface.materials.find(m => m.id === pickedId);
        if (!mat?.dmatAssetId) continue;
        bindings.push({ Key: surface.surfaceKey, DmatId: mat.dmatAssetId });
      }
    }

    const v = viewerRef.current;
    if (!v) return;
    v.sendToUnity("SwitchVariant", JSON.stringify({
      instanceId: selectedInstance.instanceId,
      key: nextKey,
      bindings,
    })).catch(err => console.warn("[SceneDesigner] SwitchVariant failed:", err));

    // Optimistic state — VariantSwitched event will confirm. Encode "back to
    // main" as undefined so existing instances stay shape-compatible.
    setInstances(prev => prev.map(i => i.instanceId === selectedInstance.instanceId
      ? { ...i, variantKey: nextKey === "main" ? undefined : nextKey }
      : i));
  }, [selectedInstance, canToggleVariant]);

  // ── PDF render actions ──────────────────────────────────────────────
  // The render-pdf-download / render-pdf-email actions read pdfLayoutId
  // from their placing block's props. Both share the same scene-state
  // snapshot logic, so we centralise it here.
  //
  // We read description1Key / description2Key / projectLabel from the cart-
  // items block in the active slot mapping so the PDF rows render the same
  // description lines the cart shows. Pulled fresh on each call rather than
  // pre-computed: the slot mapping is small and accessing it once per
  // download is cheap.
  const buildPdfInputs = useCallback(async (): Promise<import("./api/pdfRender").PdfRenderContextInputs> => {
    const layoutId = config?.layoutTemplate ?? DEFAULT_LAYOUT_ID;
    const cartMapping = mergeSlotMappings(
      getLayoutDefaultMapping(layoutId),
      coerceSlotMapping(config?.slotMappingOverride ?? null),
    );
    const cartItemsBlock = (cartMapping["modal:cart"] ?? [])
      .find(b => b.block === "cart-items");
    const ci = (cartItemsBlock?.props ?? {}) as Record<string, unknown>;

    // Warm the product-detail cache for placed products so the row's material
    // picks can resolve to display names (pdf-product-materials). Same warm-pass
    // the cart does on open; doing it here keeps the action self-contained.
    // Best-effort — rows whose detail fails just ship no materials.
    {
      const need = new Set<string>();
      for (const i of instances) {
        if (!detailCacheRef.current.has(i.productId)) need.add(i.productId);
      }
      if (need.size > 0) {
        await Promise.all(
          Array.from(need).map(async productId => {
            try {
              const d = await fetchProductDetail({ baseUrl: props.baseUrl, productId, lang: props.lang });
              cacheDetail(d);
            } catch {
              /* swallow — row just renders no materials */
            }
          }),
        );
      }
    }

    // Resolve scene dimensions from Unity on demand. The cart modal already
    // does this on open; doing it again here keeps the action self-contained
    // (action can fire from anywhere, including before the cart was ever
    // opened). Failure is non-fatal — the totals row just drops.
    let dims: { width: number; height: number; depth?: number } | null = null;
    const v = viewerRef.current;
    if (v) {
      try {
        const json = await v.callUnity("GetSceneDimensions");
        const parsed = JSON.parse(json);
        if (parsed && typeof parsed.width === "number") {
          dims = { width: parsed.width, height: parsed.height ?? 0, depth: parsed.depth };
        }
      } catch {
        /* no dims */
      }
    }

    return {
      baseUrl: props.baseUrl,
      instances,
      productById,
      detailCache: detailCacheRef.current,
      dims,
      description1Key: typeof ci.description1Key === "string" ? ci.description1Key : undefined,
      description2Key: typeof ci.description2Key === "string" ? ci.description2Key : undefined,
      measurementDecimals: typeof ci.measurementDecimals === "number" ? ci.measurementDecimals : undefined,
      decimalSeparator: config?.decimalSeparator ?? ".",
      projectLabel:    typeof ci.projectLabel    === "string" ? ci.projectLabel    : undefined,
      headlineTitle:   selectedSeries?.label ?? undefined,
      seriesLabel:       selectedSeries?.label ?? undefined,
      seriesDescription: selectedSeries?.description ?? undefined,
      // Live breakdown → per-row prices + SUBTOTAL/VAT/TOTAL PRICE named
      // totals on the PDF. Null when the host has no price source.
      breakdown: priceBreakdown,
    };
  }, [config, props.baseUrl, instances, productById, selectedSeries?.label, selectedSeries?.description, cacheDetail, priceBreakdown]);

  const triggerPdfDownload = useCallback(async (pdfLayoutId: string) => {
    if (!pdfLayoutId) {
      console.warn("[SceneDesigner] render-pdf-download fired without a pdfLayoutId — set one on the action block's props.");
      return;
    }
    showBusy("Generating PDF");
    try {
      const inputs = await buildPdfInputs();
      const { downloadPdf } = await import("./api/pdfRender");
      await downloadPdf({
        baseUrl: props.baseUrl,
        pdfLayoutId,
        inputs,
        lang: props.lang,
        callUnity: viewerRef.current?.callUnity,
      });
    } catch (err) {
      console.error("[SceneDesigner] PDF download failed:", err);
      // Surface error through busy overlay; a toast layer would be better
      // long-term but is more than this commit needs.
      alert(`PDF download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      hideBusy();
    }
  }, [buildPdfInputs, props.baseUrl, showBusy, hideBusy]);

  const openPdfEmail = useCallback((blockProps: Record<string, unknown>) => {
    const pdfLayoutId = typeof blockProps.pdfLayoutId === "string" ? blockProps.pdfLayoutId : "";
    const emailLayoutId = typeof blockProps.emailLayoutId === "string" ? blockProps.emailLayoutId : "";
    if (!pdfLayoutId && !emailLayoutId) {
      console.warn("[SceneDesigner] render-pdf-email fired without an emailLayoutId or pdfLayoutId.");
      return;
    }
    // Wipe any leftover values from a previous open so the form starts blank.
    // formId convention: the modal:pdf-email slot defaults to "default", which
    // matches what the form-submit block also defaults to. Operators who set
    // formId explicitly are responsible for keeping the two in sync.
    clearForm("default");
    openModal("pdf-email", blockProps);
  }, [openModal, clearForm]);

  const sendPdfFromForm = useCallback(async (blockProps: Record<string, unknown>) => {
    // pdfLayoutId resolution: the form-submit block's own prop wins, but if
    // it's empty we fall back to the id the opener (the cart-footer
    // render-pdf-email button) stashed in the modal payload. That lets the
    // operator set the id once on the button instead of on both the button and
    // the submit block.
    const ownId = typeof blockProps.pdfLayoutId === "string" ? blockProps.pdfLayoutId : "";
    const openerPayload = modalPayloadsRef.current.get("pdf-email") as { pdfLayoutId?: string; emailLayoutId?: string } | undefined;
    const pdfLayoutId = ownId || (openerPayload?.pdfLayoutId ?? "");
    // The email layout is the anchor when set; the PDF becomes an optional
    // attachment. Falls back to the opener's id like pdfLayoutId does.
    const ownEmailId = typeof blockProps.emailLayoutId === "string" ? blockProps.emailLayoutId : "";
    const emailLayoutId = ownEmailId || (openerPayload?.emailLayoutId ?? "");
    if (!pdfLayoutId && !emailLayoutId) {
      console.warn("[SceneDesigner] render-pdf-email-send fired without an emailLayoutId or pdfLayoutId.");
      return;
    }
    const values = (blockProps.formValues as Record<string, string> | undefined) ?? {};
    // Convention for the field names — match the default modal mapping so
    // operators don't have to wire field names if they take the default.
    // Operators who rename fields also adjust the form-submit's expectations
    // via blockProps (future enhancement); v1 is the default contract.
    const to = (values.to ?? "").trim();
    if (!to) {
      console.warn("[SceneDesigner] render-pdf-email-send: empty `to` field.");
      return;
    }
    const cc = values.cc ?? "";
    const message = values.message ?? "";
    const toEmails = to.split(",").map(s => s.trim()).filter(Boolean);
    const ccEmails = cc.split(",").map(s => s.trim()).filter(Boolean);
    showBusy("Sending email");
    try {
      const inputs = await buildPdfInputs();
      if (emailLayoutId) {
        // Email layout is the anchor: the body/subject come from the layout's
        // (translatable) blocks server-side; the PDF, if configured, is attached.
        // The operator's free-text message rides along as {{message}} for RawHtml
        // layouts / future message blocks.
        const { sendViaEmailLayout } = await import("./api/pdfRender");
        await sendViaEmailLayout({
          baseUrl: props.baseUrl,
          emailLayoutId,
          pdfLayoutId: pdfLayoutId || undefined,
          inputs,
          lang: props.lang,
          callUnity: viewerRef.current?.callUnity,
          toEmails,
          ccEmails,
        });
      } else {
        const { sendPdfEmail } = await import("./api/pdfRender");
        await sendPdfEmail({
          baseUrl: props.baseUrl,
          pdfLayoutId,
          inputs,
          lang: props.lang,
          callUnity: viewerRef.current?.callUnity,
          email: {
            toEmails,
            ccEmails,
            htmlBody: message.trim()
              ? `<p>${escapeMessageHtml(message).replace(/\n/g, "<br>")}</p>`
              : undefined,
          },
        });
      }
      // Close + clear on success so the next open starts blank.
      closeOneModal("pdf-email");
      clearForm("default");
    } catch (err) {
      console.error("[SceneDesigner] email send failed:", err);
      alert(`Email send failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      hideBusy();
    }
  }, [buildPdfInputs, props.baseUrl, showBusy, hideBusy, closeOneModal, clearForm]);

  // ── Host API for actions ────────────────────────────────────────────
  // Built fresh each render so action callbacks always see the latest state.
  // Memo only so the identity is stable within a render — useHostActions
  // dependencies key off this.
  const hostApi: ActionHostApi = useMemo(() => ({
    resetScene: handleResetScene,
    deleteSelected: handleDeleteSelected,
    sendCommand,
    applyMaterialToObject,
    toggleVariant,
    canToggleVariant,
    selectedInstanceVariantKey: selectedInstance?.variantKey,
    openModal,
    closeTopModal,
    closeAllModals,
    showBusy,
    hideBusy,
    backToSeriesPicker,
    selectedInstanceId,
    instanceCount: instances.length,
    isFpsCamera,
    getFeature,
    setFeature,
    triggerPdfDownload,
    openPdfEmail,
    sendPdfFromForm,
  }), [
    handleResetScene, handleDeleteSelected, sendCommand, applyMaterialToObject,
    toggleVariant, canToggleVariant, selectedInstance?.variantKey, openModal,
    closeTopModal, closeAllModals, showBusy, hideBusy, backToSeriesPicker,
    selectedInstanceId, instances.length, isFpsCamera, getFeature, setFeature,
    triggerPdfDownload, openPdfEmail, sendPdfFromForm,
  ]);

  const actions = useHostActions({ host: hostApi, measurementsVisible });

  // ── Startup actions ─────────────────────────────────────────────────
  // Walk the layout's `startup` slot once Unity reports ready and fire each
  // action with its placing block's props. Typical contents: one or more
  // apply-scene-materials blocks that paint named scene objects with default
  // DMats so the scene boots with the operator's chosen look instead of the
  // Unity-default materials.
  //
  // Fires once per (designer, ready-transition). The ref guards against
  // re-firing on rerenders that happen while viewerReady stays true (parent
  // state changes, modal opens, etc.).
  const startupFiredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!viewerReady || !config) return;
    const fireKey = props.designerId ?? "demo";
    if (startupFiredForRef.current === fireKey) return;
    startupFiredForRef.current = fireKey;

    const layoutIdForStartup = props.layoutTemplate ?? config.layoutTemplate ?? DEFAULT_LAYOUT_ID;
    const mapping = mergeSlotMappings(
      getLayoutDefaultMapping(layoutIdForStartup),
      coerceSlotMapping(config.slotMappingOverride)
    );
    const entries = mapping["startup"] ?? [];
    for (const b of entries) {
      if (b.block !== "action") continue;
      const p = (b.props ?? {}) as Record<string, unknown>;
      const id = typeof p.actionId === "string" ? p.actionId : "";
      if (!id) continue;
      const def = actions.get(id);
      if (!def) {
        console.warn(`[SceneDesigner] startup: action "${id}" not registered; skipping.`);
        continue;
      }
      if (def.enabled === false) continue;
      def.run?.(p);
    }
  }, [viewerReady, config, actions, props.designerId, props.layoutTemplate]);

  // ── Built-in modal frames ───────────────────────────────────────────
  // Register metadata once per session (size + dismiss flags). The renders
  // are supplied via renderOverrides below so they re-close over current
  // host state on every render — step 4 will replace these placeholder
  // renderers with slot-driven content composed from blocks.
  if (!getModal("cart")) {
    registerModal({ id: "cart",  size: "lg", dismissOnBackdrop: true });
  }
  if (!getModal("help")) {
    registerModal({ id: "help",  size: "md", dismissOnBackdrop: true });
  }
  if (!getModal("pdf-email")) {
    // Dedicated email-PDF modal — collects To/Subject/Message/Cc/Bcc and on
    // submit fires the render-pdf-email path with the operator-set
    // pdfLayoutId. Different from "email" (which is a generic placeholder
    // projects override) because this one is specific to the PdfLayout
    // email-send flow and ships with this module.
    registerModal({ id: "pdf-email", size: "md", dismissOnBackdrop: true });
  }
  if (!getModal("scene-looks")) {
    registerModal({ id: "scene-looks", size: "md", dismissOnBackdrop: true });
  }
  if (!getModal("download-model")) {
    registerModal({ id: "download-model", size: "md", dismissOnBackdrop: true });
  }
  if (!getModal("render-scene")) {
    registerModal({ id: "render-scene", size: "md", dismissOnBackdrop: true });
  }

  // ── Render ──────────────────────────────────────────────────────────
  if (loading && !config) {
    return <div className="sd-state sd-state--loading">Loading…</div>;
  }
  if (error) {
    return <div className="sd-state sd-state--error">Error: {error}</div>;
  }
  if (!config) {
    return <div className="sd-state sd-state--empty">No data.</div>;
  }

  // Canonicalise so a legacy slug (e.g. "single-menu") maps to the current
  // layout AND yields the right `sd--layout-*` CSS class.
  const layoutId = canonicalLayoutId(props.layoutTemplate ?? config.layoutTemplate);
  const LayoutComponent = pickLayout(layoutId);

  const blockHost = {
    actions,
    selectedInstanceId,
    selectedInstance,
    selectedProductName,
    instances,
    instanceCount: instances.length,
    productById,
    visibleCategories,
    products: config.products,
    activeSeries: selectedSeries,
    detailCache: detailCacheRef.current,
    detailCacheVersion,
    onCacheDetail: cacheDetail,
    getFeature,
    openModal,
    openConfirm: (opts: {
      title?: string;
      message: string;
      confirmLabel?: string;
      cancelLabel?: string;
      onConfirm: () => void;
    }) => setConfirmDialog(opts),
    baseUrl: props.baseUrl,
    applyMaterialToAll: handleApplyMaterialToAll,
    onPickMaterial: handlePickMaterial,
    // Latest rules-engine overlay (null = no rules loaded). Blocks render
    // constraints from it; the viewer pushes are handled by the host effects
    // above, so blocks never talk to Unity about forced picks themselves.
    rulesOverlay: ruleEval?.overlay ?? null,
    collectionPicks,
    onPickCollectionDefault: handlePickCollectionDefault,
    notify: notifications.notify,
    formValues,
    setFormValue,
    readFormValues,
    clearForm,
    lang: props.lang,
  };

  // Series-picker gate: when a Designer has >1 series and the user hasn't
  // picked one yet, show the picker instead of the configurator. The theme
  // is applied even on this screen so the picker carries the brand. The
  // editorial picker uses <Slot> for its eyebrow / headline / body copy, so
  // we hand it the same blockHost the configurator uses (active series is
  // null at this point, which the slot-bound text blocks tolerate).
  if (config.series.length > 1 && !selectedSeries) {
    const SeriesPickerComp = pickSeriesPicker(layoutId);
    return (
      <div className={"sd sd--layout-" + layoutId}>
        {config.themeTokens && (
          <ThemeStyle tokens={config.themeTokens} fontImportUrl={config.themeFontImportUrl} paintBodyBackground />
        )}
        <SeriesPickerComp
          series={config.series}
          onPick={setSelectedSeries}
          designerName={config.designerName ?? undefined}
          brandLogoUrl={config.brandLogoUrl}
          blockHost={blockHost}
          slotMappingOverride={coerceSlotMapping(config.slotMappingOverride)}
        />
      </div>
    );
  }

  // Generic slot-driven modal: render a simple-content modal (help, email, …)
  // from its slot's blocks. The slot's first heading text block becomes the
  // header title (and is filtered out of the body so it isn't shown twice);
  // everything else renders in the body via <Slot>. fallbackTitle is used when
  // the slot has no heading block. Mirrors the scene-looks pattern.
  const renderSlotModal = (
    ctx: { close: () => void },
    slotId: string,
    fallbackTitle: string,
  ) => {
    const mapping = mergeSlotMappings(
      getLayoutDefaultMapping(layoutId),
      coerceSlotMapping(config.slotMappingOverride),
    );
    const entries = mapping[slotId] ?? [];
    const headingBlock = entries.find(
      b => b.block === "text" && (b.props as Record<string, unknown> | undefined)?.variant === "heading",
    );
    const headingText =
      headingBlock && typeof (headingBlock.props as Record<string, unknown>).text === "string"
        ? String((headingBlock.props as Record<string, unknown>).text)
        : fallbackTitle;
    const bodyEntries = headingBlock ? entries.filter(b => b !== headingBlock) : entries;
    return (
      <div className="sd-modal__inner">
        <header className="sd-modal__header">
          <h2>{headingText}</h2>
          <button type="button" className="sd-modal__close" onClick={ctx.close} aria-label="Close">×</button>
        </header>
        <div className="sd-modal__body">
          <Slot slotId={slotId} mapping={bodyEntries} host={blockHost} />
        </div>
      </div>
    );
  };

  // Read a modal slot's authorable text blocks by key. Returns a getter that
  // yields the block's resolved `text` for a given key (the embed already
  // resolved any translatable text into `text`), or undefined when the key is
  // absent / blank — so the modal component falls back to its English default.
  // Used by the download / render modals whose presented copy is authorable.
  const modalText = (slotId: string) => {
    const mapping = mergeSlotMappings(
      getLayoutDefaultMapping(layoutId),
      coerceSlotMapping(config.slotMappingOverride),
    );
    const entries = mapping[slotId] ?? [];
    return (key: string): string | undefined => {
      const blk = entries.find(b => b.block === "text" && b.key === key);
      const text = blk && (blk.props as Record<string, unknown> | undefined)?.text;
      return typeof text === "string" && text.trim() ? text : undefined;
    };
  };

  // Per-render renderOverrides for built-in modals — closures over the latest
  // host state so cart sees current instances, email/help see current props.
  const modalRenderOverrides = {
    cart: (ctx: { close: () => void }) => {
      // The cart is assembled from several slots (modal:cart[-mobile] body,
      // modal:cart-totals, modal:cart-footer). composeCartModal is the single
      // source of truth for that assembly — the focus-mode section preview
      // (focus=section:cart) calls the same helper so the two can't drift.
      const cartMapping = mergeSlotMappings(
        getLayoutDefaultMapping(layoutId),
        coerceSlotMapping(config.slotMappingOverride)
      );
      return composeCartModal({
        mapping: cartMapping,
        host: blockHost,
        isMobile,
        onClose: ctx.close,
        baseUrl: props.baseUrl,
        lang: props.lang,
        headlineTitle: selectedSeries?.label ?? undefined,
        viewerRef,
      });
    },
    // Help is slot-driven, same as scene-looks: the modal:help slot's blocks
    // render the body, and the slot's first heading text block becomes the
    // header title (filtered out of the body to avoid duplicating it). This is
    // what makes operator-authored text actually show — previously it was
    // hardcoded and ignored the slot.
    help:  (ctx: { close: () => void }) => renderSlotModal(ctx, "modal:help",  "Help"),
    "pdf-email": (ctx: { close: () => void }) => {
      // Slot-driven: the modal:pdf-email slot's blocks render the form. The
      // form-submit block reads pdfLayoutId from its own props and the
      // collected form values fire render-pdf-email-send when clicked. The
      // payload here only drives the operator-overridable chrome.
      const payload = modalPayloadsRef.current.get("pdf-email") as
        | { emailTitle?: string; emailSubtitle?: string }
        | undefined;
      const mapping = mergeSlotMappings(
        getLayoutDefaultMapping(layoutId),
        coerceSlotMapping(config.slotMappingOverride),
      );
      // The email modal is a Section: its header (title + subtitle) is authored
      // in the modal:pdf-email-header slot and its form in modal:pdf-email. The
      // header slot's first heading text block is the title; its first body text
      // block is the subtitle. composeEmailModal assembles the two — the same
      // helper the focus-mode section preview uses, so they can't drift.
      return composeEmailModal({
        mapping,
        host: blockHost,
        onClose: ctx.close,
        fallbackTitle: payload?.emailTitle?.trim() || undefined,
        fallbackSubtitle: payload?.emailSubtitle?.trim()
          || (selectedSeries?.label ? `Finalizing specs for ${selectedSeries.label}` : undefined),
      });
    },
    // Scene-looks picker — body is composed from the modal:scene-looks slot
    // mapping (default in SingleMenuLayout.defaults.ts; Designer-overridable
    // per the usual slot rules). Each preset is an action block bound to
    // apply-scene-materials with its own dmatBindings.
    "scene-looks": (ctx: { close: () => void }) => {
      const mapping = mergeSlotMappings(
        getLayoutDefaultMapping(layoutId),
        coerceSlotMapping(config.slotMappingOverride)
      );
      const entries = mapping["modal:scene-looks"] ?? [];
      // Pull the slot's first text block as the header title so the modal has
      // a consistent "title + close, then hairline, then content" header like
      // every other modal — instead of the title living in the body with an
      // empty header above it. The remaining blocks (subtitle + presets) render
      // in the body; the heading block is filtered out to avoid duplication.
      const headingBlock = entries.find(b => b.block === "text" && (b.props as Record<string, unknown> | undefined)?.variant === "heading");
      const headingText = headingBlock && typeof (headingBlock.props as Record<string, unknown>).text === "string"
        ? String((headingBlock.props as Record<string, unknown>).text)
        : "Choose a scene";
      const bodyEntries = headingBlock ? entries.filter(b => b !== headingBlock) : entries;
      return (
        <div className="sd-modal__inner sd-modal__inner--scene-looks">
          <header className="sd-modal__header">
            <h2>{headingText}</h2>
            <button type="button" className="sd-modal__close" onClick={ctx.close} aria-label="Close">×</button>
          </header>
          <div className="sd-modal__body">
            <Slot slotId="modal:scene-looks" mapping={bodyEntries} host={blockHost} />
          </div>
        </div>
      );
    },
    // Download 3D model — fetches the current scene from Unity via callUnity,
    // then posts the resulting DModel JSON to /api/dmodel/export/{format} per
    // user-picked format. The endpoint stubs land in DAsset; until the
    // converter is wired the endpoint returns 501 and the modal surfaces it
    // as "not available yet".
    "download-model": (ctx: { close: () => void }) => {
      // Presented copy authored in the modal:download-model slot's text blocks,
      // read by key. Empty / missing keys fall back to the component defaults.
      const t = modalText("modal:download-model");
      return (
        <DownloadModelModal
          onClose={ctx.close}
          viewerRef={viewerRef}
          baseUrl={props.baseUrl}
          filenameStem={selectedSeries?.label ?? undefined}
          content={{
            heading: t("heading"),
            intro: t("intro"),
            formatDescriptions: {
              obj: t("desc-obj"),
              glb: t("desc-glb"),
              stl: t("desc-stl"),
            },
          }}
        />
      );
    },
    // Render scene — submits the DModel + a renderProfileId to
    // /api/dmodel/render and opens the resulting /render/{id} polling page
    // in a new tab. The renderProfileId is stashed on modalPayloadsRef when
    // the action block opens this modal — we look it up here.
    "render-scene": (ctx: { close: () => void }) => {
      const payload = modalPayloadsRef.current.get("render-scene") as
        | { renderProfileId?: unknown }
        | undefined;
      const renderProfileId =
        payload && typeof payload.renderProfileId === "string"
          ? payload.renderProfileId
          : undefined;
      const t = modalText("modal:render-scene");
      return (
        <RenderSceneModal
          onClose={ctx.close}
          viewerRef={viewerRef}
          baseUrl={props.baseUrl}
          renderProfileId={renderProfileId}
          content={{
            heading: t("heading"),
            intro: t("intro"),
            renderLabel: t("render-label"),
            cancelLabel: t("cancel-label"),
            doneLabel: t("done-label"),
          }}
        />
      );
    },
  };

  const layoutProps: LayoutProps = {
    visibleCategories,
    products: config.products,
    productById,
    viewer: config.viewer,
    series: config.series,
    selectedSeries,
    layoutFlags,
    designerName: config.designerName,
    brandLogoUrl: config.brandLogoUrl,
    instances,
    selectedInstance,
    selectedInstanceId,
    selectedProductName,
    viewerRef,
    viewerReady,
    setViewerReady,
    onUnityEvent: handleUnityEvent,
    baseUrl: props.baseUrl,
    apiBaseUrl: props.apiBaseUrl,
    isFpsCamera,
    measurementSetup,
    measurementsVisible,
    catalogOpen,
    setCatalogOpen,
    openModal,
    closeAllModals,
    detailCache: detailCacheRef.current,
    onCacheDetail: cacheDetail,
    actions,
    blockHost,
    slotMappingOverride: coerceSlotMapping(config.slotMappingOverride),
    sendCommand,
    onResetScene: handleResetScene,
    onDeleteSelected: handleDeleteSelected,
    onPickMaterial: handlePickMaterial,
    onCloseSelection: handleCloseSelection,
  };

  return (
    <div className={"sd sd--layout-" + layoutId}>
      {config.themeTokens && (
        <ThemeStyle tokens={config.themeTokens} fontImportUrl={config.themeFontImportUrl} paintBodyBackground />
      )}
      <LayoutComponent {...layoutProps} />
      <ActiveModalRenderer
        stack={modalStack}
        closeOne={closeOneModal}
        closeAll={closeAllModals}
        renderOverrides={modalRenderOverrides}
      />
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          cancelLabel={confirmDialog.cancelLabel}
          onConfirm={() => {
            const cb = confirmDialog.onConfirm;
            setConfirmDialog(null);
            cb();
          }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      <BusyOverlay visible={busy.visible} label={busy.label} />
      <NotificationsHost toasts={notifications.toasts} onDismiss={notifications.dismiss} />
    </div>
  );
}

// ── Response → ResolvedConfig adapters ────────────────────────────────

function designerResponseToConfig(r: DesignerConfigResponse): ResolvedConfig {
  return {
    categories: r.categories,
    products: r.products,
    viewer: r.viewer,
    series: r.series,
    layoutToggles: r.layoutToggles ?? null,
    layoutTemplate: r.layoutTemplate || DEFAULT_LAYOUT_ID,
    slotMappingOverride: (r.slotMapping ?? null) as Record<string, unknown> | null,
    themeTokens: r.theme?.tokens ?? null,
    themeFontImportUrl: r.theme?.fontImportUrl ?? null,
    designerName: r.name,
    brandLogoUrl: r.brandLogoUrl ?? null,
    decimalSeparator: r.decimalSeparator === "," ? "," : ".",
  };
}

function catalogResponseToConfig(r: SceneCatalogResponse): ResolvedConfig {
  return {
    categories: r.categories,
    products: r.products,
    viewer: r.viewer,
    series: [],
    layoutToggles: null,
    layoutTemplate: DEFAULT_LAYOUT_ID,
    slotMappingOverride: null,
    themeTokens: null,
    themeFontImportUrl: null,
    designerName: null,
    brandLogoUrl: null,
    decimalSeparator: ".",
  };
}

export default SceneDesignerTool;
